/**
 * CREATIVE DETONATOR - Async Utilities
 *
 * Provides safe async patterns:
 * - Cancellable operations with AbortController
 * - Last-call-wins for rapid interactions
 * - Polling management with timeout
 * - Background jobs that survive component destruction
 * - Component-scoped async operations
 *
 * @version 1.0.0
 * @description Implements REFACTOR-05 (Async Safety)
 */

// ============================================================================
// OPERATION REGISTRY
// ============================================================================

/** @type {Map<string, AsyncOperation>} */
const operations = new Map();

/** @type {Map<string, PollingInstance>} */
const pollingIntervals = new Map();

/**
 * @typedef {Object} AsyncOperation
 * @property {string} key
 * @property {AbortController} controller
 * @property {AbortSignal} signal
 * @property {number} startTime
 */

/**
 * Create cancellable async operation
 * @param {string} key - Unique operation key
 * @returns {AsyncOperation}
 */
export function createOperation(key) {
  // Cancel existing operation with same key
  cancelOperation(key);

  const controller = new AbortController();
  const operation = {
    key,
    controller,
    signal: controller.signal,
    startTime: Date.now(),

    /**
     * Run async function with abort signal
     * @param {Function} fn - Async function receiving signal
     * @returns {Promise}
     */
    async run(fn) {
      try {
        const result = await fn(this.signal);
        return result;
      } finally {
        operations.delete(key);
      }
    },

    /**
     * Check if operation was aborted
     * @returns {boolean}
     */
    isAborted() {
      return this.signal.aborted;
    },

    /**
     * Cancel this operation
     */
    cancel() {
      this.controller.abort();
      operations.delete(key);
    }
  };

  operations.set(key, operation);
  return operation;
}

/**
 * Cancel operation by key (supports glob patterns)
 * @param {string} key - Exact key or glob pattern (e.g., 'video-gen-*')
 */
export function cancelOperation(key) {
  if (key.includes('*')) {
    // Glob pattern
    const pattern = new RegExp('^' + key.replace(/\*/g, '.*') + '$');
    for (const [opKey, operation] of operations) {
      if (pattern.test(opKey)) {
        operation.controller.abort();
        operations.delete(opKey);
      }
    }
  } else {
    // Exact key
    const operation = operations.get(key);
    if (operation) {
      operation.controller.abort();
      operations.delete(key);
    }
  }
}

/**
 * Cancel all operations
 */
export function cancelAllOperations() {
  for (const [key, operation] of operations) {
    operation.controller.abort();
  }
  operations.clear();
}

/**
 * Get active operation keys
 * @returns {string[]}
 */
export function getActiveOperations() {
  return Array.from(operations.keys());
}

// ============================================================================
// LAST-CALL-WINS
// ============================================================================

/**
 * Create last-call-wins wrapper
 * Only the most recent call's result is used
 * @param {Function} fn - Async function to wrap
 * @param {string} [keyPrefix='lcw'] - Key prefix for operation tracking
 * @returns {Function} - Wrapped function
 */
export function lastCallWins(fn, keyPrefix = 'lcw') {
  let callId = 0;

  return async function(...args) {
    const thisCallId = ++callId;
    const key = `${keyPrefix}-${thisCallId}`;

    // Cancel previous call
    cancelOperation(`${keyPrefix}-*`);

    const operation = createOperation(key);

    try {
      const result = await operation.run(async (signal) => {
        // Pass signal as part of options object if last arg is object
        const lastArg = args[args.length - 1];
        if (lastArg && typeof lastArg === 'object' && !Array.isArray(lastArg)) {
          args[args.length - 1] = { ...lastArg, signal };
        } else {
          args.push({ signal });
        }
        return await fn.apply(this, args);
      });

      // Check if this is still the latest call
      if (thisCallId !== callId) {
        return undefined;
      }

      return result;
    } catch (err) {
      // Suppress abort errors
      if (err.name === 'AbortError') {
        return undefined;
      }
      throw err;
    }
  };
}

// ============================================================================
// POLLING
// ============================================================================

/**
 * @typedef {Object} PollingInstance
 * @property {string} key
 * @property {number} intervalId
 * @property {AbortController} controller
 * @property {number} startTime
 * @property {Function} stop
 */

/**
 * Polling manager
 */
export const Polling = {
  /**
   * Start polling
   * @param {string} key - Unique polling key
   * @param {Function} fn - Polling function (receives signal)
   * @param {number} interval - Interval in ms
   * @param {Object} [options]
   * @param {number} [options.timeout=300000] - Max polling duration (5 min default)
   * @param {Function} [options.onTimeout] - Called on timeout
   * @returns {PollingInstance}
   */
  start(key, fn, interval, options = {}) {
    // Stop existing polling with same key
    this.stop(key);

    const controller = new AbortController();
    const startTime = Date.now();
    const timeout = options.timeout || 5 * 60 * 1000;

    const intervalId = setInterval(async () => {
      // Check timeout
      if (Date.now() - startTime > timeout) {
        this.stop(key);
        options.onTimeout?.();
        return;
      }

      // Check if aborted
      if (controller.signal.aborted) {
        clearInterval(intervalId);
        return;
      }

      try {
        await fn(controller.signal);
      } catch (err) {
        if (err.name === 'AbortError') {
          this.stop(key);
        } else {
          console.error(`[Polling] Error (${key}):`, err);
        }
      }
    }, interval);

    const polling = {
      key,
      intervalId,
      controller,
      startTime,

      stop() {
        controller.abort();
        clearInterval(intervalId);
        pollingIntervals.delete(key);
      }
    };

    pollingIntervals.set(key, polling);
    return polling;
  },

  /**
   * Stop polling by key (supports glob patterns)
   * @param {string} key - Exact key or glob pattern
   */
  stop(key) {
    if (key.includes('*')) {
      const pattern = new RegExp('^' + key.replace(/\*/g, '.*') + '$');
      for (const [pollKey, polling] of pollingIntervals) {
        if (pattern.test(pollKey)) {
          polling.stop();
        }
      }
    } else {
      const polling = pollingIntervals.get(key);
      if (polling) {
        polling.stop();
      }
    }
  },

  /**
   * Stop all polling
   */
  stopAll() {
    for (const [key, polling] of pollingIntervals) {
      polling.stop();
    }
    pollingIntervals.clear();
  },

  /**
   * Check if polling is active
   * @param {string} key
   * @returns {boolean}
   */
  isActive(key) {
    return pollingIntervals.has(key);
  },

  /**
   * Get active polling keys
   * @returns {string[]}
   */
  getActive() {
    return Array.from(pollingIntervals.keys());
  }
};

// ============================================================================
// BACKGROUND JOBS (Survive Component Destruction)
// ============================================================================

const JOBS_STORAGE_KEY = 'cd_active_jobs';

/** @type {Map<string, BackgroundJob>} */
const activeJobs = new Map();

/**
 * @typedef {Object} BackgroundJob
 * @property {string} id
 * @property {Object} data
 * @property {number} startTime
 * @property {string} status
 * @property {Object} handlers
 * @property {number|null} pollingInterval
 * @property {AbortController} abortController
 */

/**
 * Background Job Manager
 * Manages long-running jobs that persist beyond component lifecycle.
 */
export const JobManager = {
  /**
   * Register a background job
   * @param {string} jobId - Unique job ID
   * @param {Object} jobData - Job metadata for persistence
   * @param {Object} [handlers] - Job control handlers
   * @param {Function} [handlers.onComplete] - Called when job completes
   * @param {Function} [handlers.onFail] - Called when job fails
   * @param {Function} [handlers.onCancel] - Called when job is cancelled
   * @returns {Object} - Job control interface
   */
  register(jobId, jobData, handlers = {}) {
    const job = {
      id: jobId,
      data: jobData,
      startTime: Date.now(),
      status: 'running',
      handlers,
      pollingInterval: null,
      abortController: new AbortController()
    };

    activeJobs.set(jobId, job);
    this.persist();

    return {
      id: jobId,
      signal: job.abortController.signal,
      cancel: () => this.cancel(jobId),
      complete: (result) => this.complete(jobId, result),
      fail: (error) => this.fail(jobId, error)
    };
  },

  /**
   * Start polling for a job
   * @param {string} jobId
   * @param {Function} pollFn - Polling function (receives signal)
   * @param {number} [interval=4000] - Poll interval in ms
   */
  startPolling(jobId, pollFn, interval = 4000) {
    const job = activeJobs.get(jobId);
    if (!job) return;

    // Clear any existing polling
    if (job.pollingInterval) {
      clearInterval(job.pollingInterval);
    }

    job.pollingInterval = setInterval(async () => {
      if (job.abortController.signal.aborted) {
        clearInterval(job.pollingInterval);
        return;
      }

      try {
        await pollFn(job.abortController.signal);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error(`[JobManager] Poll error for ${jobId}:`, err);
        }
      }
    }, interval);
  },

  /**
   * Cancel a job
   * @param {string} jobId
   */
  cancel(jobId) {
    const job = activeJobs.get(jobId);
    if (!job) return;

    job.status = 'cancelled';
    job.abortController.abort();
    if (job.pollingInterval) {
      clearInterval(job.pollingInterval);
    }

    activeJobs.delete(jobId);
    this.persist();

    job.handlers.onCancel?.();
  },

  /**
   * Mark job as complete
   * @param {string} jobId
   * @param {*} result
   */
  complete(jobId, result) {
    const job = activeJobs.get(jobId);
    if (!job) return;

    job.status = 'completed';
    if (job.pollingInterval) {
      clearInterval(job.pollingInterval);
    }

    activeJobs.delete(jobId);
    this.persist();

    job.handlers.onComplete?.(result);
  },

  /**
   * Mark job as failed
   * @param {string} jobId
   * @param {*} error
   */
  fail(jobId, error) {
    const job = activeJobs.get(jobId);
    if (!job) return;

    job.status = 'failed';
    if (job.pollingInterval) {
      clearInterval(job.pollingInterval);
    }

    activeJobs.delete(jobId);
    this.persist();

    job.handlers.onFail?.(error);
  },

  /**
   * Persist active jobs to localStorage
   */
  persist() {
    const toSave = {};
    for (const [id, job] of activeJobs) {
      toSave[id] = {
        data: job.data,
        startTime: job.startTime,
        status: job.status
      };
    }
    try {
      localStorage.setItem(JOBS_STORAGE_KEY, JSON.stringify(toSave));
    } catch (err) {
      console.warn('[JobManager] Failed to persist jobs:', err);
    }
  },

  /**
   * Resume jobs from localStorage (call on app init)
   * @param {Function} resumeHandler - Called for each saved job
   */
  async resume(resumeHandler) {
    try {
      const raw = localStorage.getItem(JOBS_STORAGE_KEY);
      if (!raw) return;

      const savedJobs = JSON.parse(raw);
      const TIMEOUT = 10 * 60 * 1000; // 10 minutes
      const jobsToKeep = {};

      for (const [id, savedJob] of Object.entries(savedJobs)) {
        // Skip and discard timed-out jobs
        if (Date.now() - savedJob.startTime > TIMEOUT) {
          console.log(`[JobManager] Job ${id} timed out, removing`);
          continue;
        }

        // Keep job in storage until it completes/fails/cancels
        jobsToKeep[id] = savedJob;

        // Let caller handle resume logic
        await resumeHandler(id, savedJob);
      }

      // Only keep non-timed-out jobs (don't clear everything!)
      // Jobs will be removed individually when they complete/fail/cancel
      if (Object.keys(jobsToKeep).length > 0) {
        localStorage.setItem(JOBS_STORAGE_KEY, JSON.stringify(jobsToKeep));
      } else {
        localStorage.removeItem(JOBS_STORAGE_KEY);
      }
    } catch (err) {
      console.error('[JobManager] Resume error:', err);
    }
  },

  /**
   * Get job by ID
   * @param {string} jobId
   * @returns {BackgroundJob|undefined}
   */
  get(jobId) {
    return activeJobs.get(jobId);
  },

  /**
   * Check if job is active
   * @param {string} jobId
   * @returns {boolean}
   */
  isActive(jobId) {
    return activeJobs.has(jobId);
  },

  /**
   * Get all active jobs
   * @returns {BackgroundJob[]}
   */
  getAll() {
    return Array.from(activeJobs.values());
  },

  /**
   * Cancel all jobs
   */
  cancelAll() {
    for (const [id] of activeJobs) {
      this.cancel(id);
    }
  }
};

// ============================================================================
// DEBOUNCE WITH ABORT
// ============================================================================

/**
 * Debounce with abort support
 * @param {Function} fn - Async function to debounce
 * @param {number} [delay=300] - Delay in ms
 * @returns {Function} - Debounced function with cancel() and flush() methods
 */
export function debounceAsync(fn, delay = 300) {
  let timeoutId = null;
  let currentController = null;

  const debounced = async function(...args) {
    // Cancel previous
    if (currentController) {
      currentController.abort();
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    currentController = new AbortController();
    const signal = currentController.signal;

    return new Promise((resolve, reject) => {
      timeoutId = setTimeout(async () => {
        try {
          if (signal.aborted) return;
          const result = await fn.apply(this, args);
          if (!signal.aborted) {
            resolve(result);
          }
        } catch (err) {
          if (!signal.aborted) {
            reject(err);
          }
        }
      }, delay);
    });
  };

  debounced.cancel = () => {
    if (timeoutId) clearTimeout(timeoutId);
    if (currentController) currentController.abort();
    timeoutId = null;
    currentController = null;
  };

  debounced.flush = async function(...args) {
    debounced.cancel();
    return fn.apply(this, args);
  };

  return debounced;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Wrap promise with abort signal
 * @param {Promise} promise
 * @param {AbortSignal} signal
 * @returns {Promise}
 */
export async function withSignal(promise, signal) {
  if (signal.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  return new Promise((resolve, reject) => {
    const onAbort = () => {
      reject(new DOMException('Aborted', 'AbortError'));
    };

    signal.addEventListener('abort', onAbort, { once: true });

    promise
      .then(result => {
        signal.removeEventListener('abort', onAbort);
        if (!signal.aborted) {
          resolve(result);
        }
      })
      .catch(err => {
        signal.removeEventListener('abort', onAbort);
        if (!signal.aborted) {
          reject(err);
        }
      });
  });
}

/**
 * Run function only if not aborted
 * @param {AbortSignal} signal
 * @param {Function} fn
 * @returns {*}
 */
export async function ifNotAborted(signal, fn) {
  if (signal.aborted) return;
  return fn();
}

/**
 * Sleep with abort support
 * @param {number} ms - Duration in milliseconds
 * @param {AbortSignal} [signal] - Optional abort signal
 * @returns {Promise<void>}
 */
export function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timeout = setTimeout(resolve, ms);

    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(timeout);
        reject(new DOMException('Aborted', 'AbortError'));
      }, { once: true });
    }
  });
}

/**
 * Retry with exponential backoff
 * @param {Function} fn - Async function to retry
 * @param {Object} [options]
 * @param {number} [options.maxRetries=3]
 * @param {number} [options.baseDelay=1000]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise}
 */
export async function retry(fn, options = {}) {
  const { maxRetries = 3, baseDelay = 1000, signal } = options;

  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    try {
      return await fn(signal);
    } catch (err) {
      lastError = err;

      if (err.name === 'AbortError') {
        throw err;
      }

      if (attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt);
        await sleep(delay, signal);
      }
    }
  }

  throw lastError;
}

// ============================================================================
// CLEANUP ON PAGE UNLOAD
// ============================================================================

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    cancelAllOperations();
    Polling.stopAll();
    // Note: Jobs are NOT cancelled - they persist
  });

  // Debug utilities (only in dev)
  if (import.meta.env?.DEV) {
    window.__asyncDebug = {
      getOperations: getActiveOperations,
      getPolling: () => Polling.getActive(),
      getJobs: () => JobManager.getAll(),
      cancelAll: cancelAllOperations,
      stopAllPolling: () => Polling.stopAll(),
      cancelAllJobs: () => JobManager.cancelAll()
    };
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  createOperation,
  cancelOperation,
  cancelAllOperations,
  getActiveOperations,
  lastCallWins,
  Polling,
  JobManager,
  debounceAsync,
  withSignal,
  ifNotAborted,
  sleep,
  retry
};
